// @flow
import { noop } from 'lodash';
import React, {
    createContext,
    FunctionComponent,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

type HashParamKey = string;
type GetHashParam = (key: HashParamKey) => string;
type SetHashParam = (key: HashParamKey, value: string | number | boolean | null) => void;

type HashParamsType = {
    getHashParam: GetHashParam,
    setHashParam: SetHashParam,
    initialized: boolean,
    pristineOnFirstAccess?: boolean, // this tracks whether if the URL had any param on first app render or not
};

const Context = createContext<HashParamsType>({
    getHashParam: () => null,
    setHashParam: noop,
    initialized: false,
});

const replaceHashParams = (params: URLSearchParams, replacement) => {
    Object.keys(replacement).forEach((key) => params.set(key, `${replacement[key]}`));
    return params;
};

type GetCurrentPageLink = (params?: { [key: string]: string | number }) => Promise<string>;

const HashParamsProvider: FunctionComponent<PropsWithChildren<unknown>> = ({ children }) => {
    const [initialized, setInitialized] = useState<boolean>(false);
    const [pristineOnFirstAccess, setPristineOnFirstAccess] = useState<boolean>();
    const [params, setParams] = useState<URLSearchParams>();
    const getHashParam = useCallback((key: HashParamKey) => params?.get?.(key), [params]);
    const setHashParam = useCallback(
        (key: HashParamKey, value: string | number | boolean | null) =>
            setParams((prev) => {
                const previousParamValue = prev.get(key);
                if (previousParamValue === value || previousParamValue === `${value}`) {
                    return prev;
                }
                const next = new URLSearchParams(prev);
                if (value === null || value === undefined) {
                    next.delete(key);
                } else {
                    next.set(key, `${value}`);
                }
                return next;
            }),
        [setParams],
    );

    const getCurrentPageLink = useCallback<GetCurrentPageLink>(
        (withParams): Promise<string> =>
            window.PlatformAdapter.getCurrentUrl()
                .then((location) => {
                    if (Object.keys(withParams || {}).length === 0) {
                        return location;
                    }
                    const url = new URL(location);
                    const hashParams = decodeURIComponent(
                        replaceHashParams(new URLSearchParams((url.hash || '').substring(1)), withParams).toString(),
                    );
                    return `${url.origin}${url.pathname}${url.search ?? ''}#${hashParams}`;
                })
                .catch((err) => {
                    if (err instanceof Error) throw err;
                    throw new Error(String(err));
                }),
        [],
    );

    useEffect(() => {
        if (window?.PlatformAdapter && initialized) {
            window.PlatformAdapter.updateRouteParams(params);
        }
    }, [initialized, params]);

    const value = useMemo(
        () => ({ getHashParam, setHashParam, initialized, pristineOnFirstAccess, getCurrentPageLink }),
        [getHashParam, initialized, setHashParam, pristineOnFirstAccess, getCurrentPageLink],
    );
    useEffect(() => {
        (async () => {
            if (typeof window?.PlatformAdapter?.getRouteParams === 'function') {
                const routeParams = await window.PlatformAdapter.getRouteParams();
                setPristineOnFirstAccess(routeParams.toString().length === 0);
                setParams(routeParams);
                setInitialized(true);
            }
        })();
    }, []);
    return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useHashParams = (): HashParamsType => useContext(Context);

export const withHashParams = (Component: React.ComponentType<P>) => {
    const WithHashParams = (props) => {
        return (
            <HashParamsProvider>
                <Component {...props} />
            </HashParamsProvider>
        );
    };
    return WithHashParams;
};

export default HashParamsProvider;
